import APIService from './APIService';

class AdminAPI extends APIService {
    constructor() {
        super(); // uses APIService's own default, which reads the shared config
    }

    // Dashboard and Statistics
    async getDashboardStats() {
        return this.get('/admin/dashboard-stats');
    }

    async getUserStats() {
        return this.get('/admin/user-stats');
    }

    async getSystemStats() {
        return this.get('/admin/system-stats');
    }

    // User Management (all automatically scoped to admin's school)
    async getUsers(params = {}) {
        return this.getWithParams('/admin/users', params);
    }

    async getStaffUsers() {
        return this.get('/admin/get-users');
    }

    async createUser(userData) {
        return this.post('/admin/users', userData);
    }

    async updateUser(userId, userData) {
        return this.put(`/admin/users/${userId}`, userData);
    }

    async deleteUser(userId) {
        return this.delete(`/admin/users/${userId}`);
    }

    async getUserById(userId) {
        return this.get(`/admin/users/${userId}`);
    }

    // Student Management
    async getStudents(params = {}) {
        return this.getWithParams('/admin/students', params);
    }

    async getStudentById(studentId) {
        return this.get(`/admin/students/${studentId}`);
    }

    async getStudentReports(studentId) {
        return this.get(`/admin/students/${studentId}/reports`);
    }

    async getStudentProgress(studentId) {
        return this.get(`/admin/students/${studentId}/progress`);
    }

    async updateStudentProfile(studentId, data) {
        return this.put(`/admin/students/${studentId}/profile`, data);
    }

    async fixStudentDataIssues() {
        return this.post('/admin/fix-student-data-issues', {});
    }

    async updateStudentStatus(studentId, status) {
        return this.patch(`/admin/students/${studentId}/status`, { status });
    }

    // Teacher Management
    async getTeachers(params = {}) {
        return this.getWithParams('/admin/teachers', params);
    }

    async getTeacherById(teacherId) {
        return this.get(`/admin/teachers/${teacherId}`);
    }

    async getTeacherCourses(teacherId) {
        return this.get(`/admin/teachers/${teacherId}/courses`);
    }

    async getTeacherStudents(teacherId) {
        return this.get(`/admin/teachers/${teacherId}/students`);
    }

    async updateTeacherStatus(teacherId, status) {
        return this.patch(`/admin/teachers/${teacherId}/status`, { status });
    }

    // Parent Management
    async getParents(params = {}) {
        return this.getWithParams('/admin/parents', params);
    }

    async getParentById(parentId) {
        return this.get(`/admin/parents/${parentId}`);
    }

    async getParentChildren(parentId) {
        return this.get(`/admin/parents/${parentId}/children`);
    }

    async updateParentStatus(parentId, status) {
        return this.patch(`/admin/parents/${parentId}/status`, { status });
    }

    // Course Management
    async getCourses(params = {}) {
        return this.getWithParams('/admin/courses', params);
    }

    async getCourseById(courseId) {
        return this.get(`/admin/courses/${courseId}`);
    }

    async createCourse(courseData) {
        return this.post('/admin/courses', courseData);
    }

    async updateCourse(courseId, courseData) {
        return this.put(`/admin/courses/${courseId}`, courseData);
    }

    async deleteCourse(courseId) {
        return this.delete(`/admin/courses/${courseId}`);
    }

    // Leaderboard (school-specific)
    async getLeaderboard(params = {}) {
        return this.getWithParams('/admin/leaderboard', params);
    }

    async getTopStudents(limit = 10) {
        return this.getWithParams('/admin/leaderboard/top-students', { limit });
    }

    async getClassRankings(classId) {
        return this.get(`/admin/leaderboard/class/${classId}`);
    }

    // Reports and Analytics
    async getSchoolReports(params = {}) {
        return this.getWithParams('/admin/reports', params);
    }

    async getAcademicReport(params = {}) {
        return this.getWithParams('/admin/reports/academic', params);
    }

    async getAttendanceReport(params = {}) {
        return this.getWithParams('/admin/reports/attendance', params);
    }

    async getPerformanceReport(params = {}) {
        return this.getWithParams('/admin/reports/performance', params);
    }

    async exportReport(reportType, format = 'pdf') {
        return this.downloadFile(`/admin/reports/${reportType}/export?format=${format}`, `${reportType}_report.${format}`);
    }

    // Feedback Management
    async getFeedback(params = {}) {
        return this.getWithParams('/admin/feedback', params);
    }

    async getFeedbackById(feedbackId) {
        return this.get(`/admin/feedback/${feedbackId}`);
    }

    async respondToFeedback(feedbackId, response) {
        return this.post(`/admin/feedback/${feedbackId}/respond`, { response });
    }

    async updateFeedbackStatus(feedbackId, status) {
        return this.patch(`/admin/feedback/${feedbackId}/status`, { status });
    }

    // Class Management
    async getClasses(params = {}) {
        return this.getWithParams('/admin/classes', params);
    }

    async getClassById(classId) {
        return this.get(`/admin/classes/${classId}`);
    }

    async createClass(classData) {
        return this.post('/admin/classes', classData);
    }

    async updateClass(classId, classData) {
        return this.put(`/admin/classes/${classId}`, classData);
    }

    async deleteClass(classId) {
        return this.delete(`/admin/classes/${classId}`);
    }

    async getClassStudents(classId) {
        return this.get(`/admin/classes/${classId}/students`);
    }

    async addStudentToClass(classId, studentId) {
        return this.post(`/admin/classes/${classId}/students`, { student_id: studentId });
    }

    async removeStudentFromClass(classId, studentId) {
        return this.delete(`/admin/classes/${classId}/students/${studentId}`);
    }

    // CBSE Circulars
    async getCbseCirculars(params = {}) {
        return this.getWithParams('/admin/cbse-circulars', params);
    }

    async getCbseCircularChanges() {
        return this.get('/admin/cbse-circulars/changes');
    }

    async refreshCbseCirculars() {
        return this.post('/admin/cbse-circulars/refresh', {});
    }

    async markCbseCircularsSeen(scrapeId = null) {
        return this.post('/admin/cbse-circulars/mark-seen', { scrape_id: scrapeId });
    }

    // Assignment Management
    async getAssignments(params = {}) {
        return this.getWithParams('/admin/assignments', params);
    }

    async getAssignmentById(assignmentId) {
        return this.get(`/admin/assignments/${assignmentId}`);
    }

    async getAssignmentSubmissions(assignmentId) {
        return this.get(`/admin/assignments/${assignmentId}/submissions`);
    }

    // Assessment Management
    async getAssessments(params = {}) {
        return this.getWithParams('/admin/assessments', params);
    }

    async getAssessmentById(assessmentId) {
        return this.get(`/admin/assessments/${assessmentId}`);
    }

    async getAssessmentResults(assessmentId) {
        return this.get(`/admin/assessments/${assessmentId}/results`);
    }

    // Attendance Management
    async getAttendance(params = {}) {
        return this.getWithParams('/admin/attendance', params);
    }

    async getStudentAttendance(studentId, params = {}) {
        return this.getWithParams(`/admin/students/${studentId}/attendance`, params);
    }

    async getClassAttendance(classId, params = {}) {
        return this.getWithParams(`/admin/classes/${classId}/attendance`, params);
    }

    // Notification Management
    async getNotifications(params = {}) {
        return this.getWithParams('/admin/notifications', params);
    }

    async createNotification(notificationData) {
        return this.post('/admin/notifications', notificationData);
    }

    async markNotificationAsRead(notificationId) {
        return this.patch(`/admin/notifications/${notificationId}/read`);
    }

    async deleteNotification(notificationId) {
        return this.delete(`/admin/notifications/${notificationId}`);
    }

    // School Settings
    async getSchoolSettings() {
        return this.get('/admin/school/settings');
    }

    async updateSchoolSettings(settings) {
        return this.put('/admin/school/settings', settings);
    }

    async getSchoolInfo() {
        return this.get('/admin/school/info');
    }

    async updateSchoolInfo(info) {
        return this.put('/admin/school/info', info);
    }

    // File Management
    async uploadFile(file, category = 'general') {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', category);
        return this.uploadFile('/admin/files/upload', formData);
    }

    async getFiles(params = {}) {
        return this.getWithParams('/admin/files', params);
    }

    async deleteFile(fileId) {
        return this.delete(`/admin/files/${fileId}`);
    }

    // Backup and Export
    async createBackup() {
        return this.post('/admin/backup/create');
    }

    async getBackups() {
        return this.get('/admin/backup/list');
    }

    async downloadBackup(backupId) {
        return this.downloadFile(`/admin/backup/${backupId}/download`, `backup_${backupId}.zip`);
    }

    async exportSchoolData(format = 'json') {
        return this.downloadFile(`/admin/export/school-data?format=${format}`, `school_data.${format}`);
    }

    // Legacy compatibility methods (updated to support all admin panel roles)
    async legacyLogin(email, password) {
        try {
            const response = await fetch(`${this.baseURL}/login/admin-panel`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    username: email,
                    password: password
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Login failed');
            }

            return response.json();
        } catch (error) {
            console.error('Legacy login failed:', error);
            throw error;
        }
    }

    // Health check
    async checkHealth() {
        try {
            const response = await fetch(`${this.baseURL}/health`);
            return response.ok;
        } catch (error) {
            console.error('Health check failed:', error);
            return false;
        }
    }

    // ── SDP (School Development Portal) ──────────────────────────────────────

    async sdpGetModule(moduleKey, academicYear) {
        return this.get(`/admin/sdp/module/${moduleKey}?academic_year=${encodeURIComponent(academicYear)}`);
    }

    async sdpSaveModule(moduleKey, academicYear, data, completionPercent = 0, sqaafAuditId = null) {
        const body = {
            module_key: moduleKey,
            academic_year: academicYear,
            data,
            completion_percent: completionPercent,
        };
        if (sqaafAuditId) body.sqaaf_audit_id = sqaafAuditId;
        return this.put(`/admin/sdp/module/${moduleKey}`, body);
    }

    async sdpDeleteModule(moduleKey, academicYear) {
        return this.delete(`/admin/sdp/module/${moduleKey}?academic_year=${encodeURIComponent(academicYear)}`);
    }

    async sdpGetExpenditureCatalog() {
        return this.get('/admin/sdp/expenditure/catalog');
    }

    async sdpGetExpenditure(academicYear) {
        return this.get(`/admin/sdp/expenditure?academic_year=${encodeURIComponent(academicYear)}`);
    }

    async sdpCreateExpenditure(academicYear, item) {
        return this.post(`/admin/sdp/expenditure?academic_year=${encodeURIComponent(academicYear)}`, item);
    }

    async sdpUpdateExpenditure(itemId, academicYear, item) {
        return this.patch(`/admin/sdp/expenditure/${encodeURIComponent(itemId)}?academic_year=${encodeURIComponent(academicYear)}`, item);
    }

    async sdpDeleteExpenditure(itemId, academicYear) {
        return this.delete(`/admin/sdp/expenditure/${encodeURIComponent(itemId)}?academic_year=${encodeURIComponent(academicYear)}`);
    }

    async sdpLoadExpenditureSample(academicYear) {
        return this.post(`/admin/sdp/expenditure/load-sample?academic_year=${encodeURIComponent(academicYear)}`, {});
    }

    async sdpGetBulk(academicYear) {
        return this.get(`/admin/sdp/bulk?academic_year=${encodeURIComponent(academicYear)}`);
    }

    async sdpGetCompletions(academicYear) {
        return this.get(`/admin/sdp/completions?academic_year=${encodeURIComponent(academicYear)}`);
    }

    async sdpGetGoals(academicYear) {
        return this.get(`/admin/sdp/goals?academic_year=${encodeURIComponent(academicYear)}`);
    }

    async sdpSaveGoals(academicYear, goals, sqaafAuditId = null) {
        const body = { academic_year: academicYear, goals };
        if (sqaafAuditId) body.sqaaf_audit_id = sqaafAuditId;
        return this.put('/admin/sdp/goals', body);
    }

    async sdpGenerateGoals(academicYear) {
        return this.get(`/admin/sdp/goals/generate?academic_year=${encodeURIComponent(academicYear)}`);
    }

    async sdpGetActions(academicYear) {
        return this.get(`/admin/sdp/actions?academic_year=${encodeURIComponent(academicYear)}`);
    }

    async sdpSaveActions(academicYear, actions) {
        return this.put('/admin/sdp/actions', { academic_year: academicYear, actions });
    }

    async sdpGetSettings() {
        return this.get('/admin/sdp/settings');
    }

    async sdpSaveSettings(schoolName, academicYear) {
        return this.put('/admin/sdp/settings', { schoolName, academicYear });
    }

    async sdpGetActivity(limit = 100) {
        return this.get(`/admin/sdp/activity?limit=${limit}`);
    }

    async sdpAppendActivity(module, action, status = 'Saved', statusClass = 'green') {
        return this.post('/admin/sdp/activity', { module, action, status, status_class: statusClass });
    }

    // ── SDP ↔ SQAAF Integration ───────────────────────────────────────────────

    async sdpGetSQAAFStatus(academicYear) {
        return this.get(`/admin/sdp/sqaaf-status?academic_year=${encodeURIComponent(academicYear)}`);
    }

    async sdpGenerateGoalsFromSQAAF(academicYear) {
        return this.get(`/admin/sdp/goals/generate-from-sqaaf?academic_year=${encodeURIComponent(academicYear)}`);
    }

    async sdpGenerateSWOTFromSQAAF(academicYear) {
        return this.get(`/admin/sdp/swot/generate-from-sqaaf?academic_year=${encodeURIComponent(academicYear)}`);
    }

    async sdpGenerateMarketInsightsFromIdentity(academicYear) {
        return this.get(`/admin/sdp/market/generate-from-identity?academic_year=${encodeURIComponent(academicYear)}`);
    }

    async sdpGenerateIdentityDraft(schoolName, schoolType, description) {
        return this.post('/admin/sdp/identity/generate-draft', {
            school_name: schoolName,
            school_type: schoolType,
            description,
        });
    }

    async sdpGenerateQuickStartDraft(schoolName, schoolType, description) {
        return this.post('/admin/sdp/quick-start/generate', {
            school_name: schoolName,
            school_type: schoolType,
            description,
        });
    }

    async sqaafGetCurrentAudit(academicYear) {
        const suffix = academicYear ? `?academic_year=${encodeURIComponent(academicYear)}` : "";
        return this.get(`/admin/sqaaf/audit/current${suffix}`);
    }

    async sqaafGetFrameworkOverview() {
        return this.get('/admin/sqaaf/framework/overview');
    }

    async sqaafGetFullActionPlan(academicYear) {
        const suffix = academicYear ? `?academic_year=${encodeURIComponent(academicYear)}` : "";
        return this.get(`/admin/sqaaf/audit/action-plan/full${suffix}`);
    }

    // ── SDP Plans Manager ─────────────────────────────────────────────────────

    async sdpGetPlans() {
        return this.get('/admin/sdp/plans');
    }

    async sdpCreatePlan(academicYear, label = null, setActive = true) {
        return this.post('/admin/sdp/plans/new', { academic_year: academicYear, label, set_active: setActive });
    }

    async sdpActivatePlan(academicYear) {
        return this.patch(`/admin/sdp/plans/${encodeURIComponent(academicYear)}/activate`);
    }

    // ── ACPP (Plans + Module Data) ──────────────────────────────────────────

    async acppGetSettings() {
        return this.get('/admin/acpp/settings');
    }

    async acppSaveSettings(academicYear) {
        return this.put('/admin/acpp/settings', { academic_year: academicYear });
    }

    async acppGetPlans() {
        return this.get('/admin/acpp/plans');
    }

    async acppCreatePlan(academicYear, label = null, setActive = true) {
        return this.post('/admin/acpp/plans/new', {
            academic_year: academicYear,
            label,
            set_active: setActive,
        });
    }

    async acppActivatePlan(academicYear) {
        return this.patch(`/admin/acpp/plans/${encodeURIComponent(academicYear)}/activate`);
    }

    async acppGetModuleExtras(moduleKey, academicYear) {
        return this.get(`/admin/acpp/module/${moduleKey}/extras?academic_year=${encodeURIComponent(academicYear)}`);
    }

    async acppSaveModuleExtras(moduleKey, academicYear, priority) {
        return this.put(`/admin/acpp/module/${moduleKey}/extras`, {
            module_key: moduleKey,
            academic_year: academicYear,
            priority,
        });
    }

    async acppDeleteModuleExtras(moduleKey, academicYear) {
        return this.delete(`/admin/acpp/module/${moduleKey}/extras?academic_year=${encodeURIComponent(academicYear)}`);
    }

    async acppGetModuleData(moduleKey, academicYear) {
        return this.get(`/admin/acpp/module/${moduleKey}?academic_year=${encodeURIComponent(academicYear)}`);
    }

    async acppSaveModuleData(moduleKey, academicYear, payload = {}) {
        return this.put(`/admin/acpp/module/${moduleKey}`, {
            module_key: moduleKey,
            academic_year: academicYear,
            ...payload,
        });
    }

    async acppDeleteModuleData(moduleKey, academicYear) {
        return this.delete(`/admin/acpp/module/${moduleKey}?academic_year=${encodeURIComponent(academicYear)}`);
    }
    async acppGetModule13Execution(academicYear) {
        const q = academicYear ? `?academic_year=${encodeURIComponent(academicYear)}` : '';
        return this.get(`/admin/acpp/module13/execution${q}`);
    }

    async acppAutoGenerateModule13(academicYear) {
        const q = academicYear ? `?academic_year=${encodeURIComponent(academicYear)}` : '';
        return this.post(`/admin/acpp/module13/auto-generate-from-module6${q}`, {});
    }

    async acppAutoGenerateModule7(academicYear) {
        const q = academicYear ? `?academic_year=${encodeURIComponent(academicYear)}` : '';
        return this.post(`/admin/acpp/module7/auto-generate-from-module6${q}`, {});
    }

    async acppGetModule7Execution(academicYear) {
        const q = academicYear ? `?academic_year=${encodeURIComponent(academicYear)}` : '';
        return this.get(`/admin/acpp/module7/execution${q}`);
    }

    async acppGetReferenceStaff() {
        return this.get('/admin/acpp/reference/staff');
    }

    async acppGetReferenceCompetencies(grade = "", domain = "") {
        const params = [];
        if (grade) params.push(`grade=${encodeURIComponent(grade)}`);
        if (domain) params.push(`domain=${encodeURIComponent(domain)}`);
        const suffix = params.length ? `?${params.join("&")}` : "";
        return this.get(`/admin/acpp/reference/competencies${suffix}`);
    }

    async acppGetLessonPlanDomains(grade = "") {
        const suffix = grade ? `?grade=${encodeURIComponent(grade)}` : "";
        return this.get(`/admin/acpp/reference/lessonplan-domains${suffix}`);
    }

    async acppGetLessonPlanCompetencies(grade = "", domain = "") {
        const params = [];
        if (grade) params.push(`grade=${encodeURIComponent(grade)}`);
        if (domain) params.push(`domain=${encodeURIComponent(domain)}`);
        const suffix = params.length ? `?${params.join("&")}` : "";
        return this.get(`/admin/acpp/reference/lessonplan-competencies${suffix}`);
    }

    async acppProvisionModule5(payload = {}) {
        return this.post('/admin/acpp/provision/module5', payload);
    }

    async acppProvisionModule7Assessments(payload = {}) {
        return this.post('/admin/acpp/provision/module7-assessments', payload);
    }

    async acppGetModule6CopyablePlans(academicYear, excludeUid = "") {
        const params = [`academic_year=${encodeURIComponent(academicYear)}`];
        if (excludeUid) params.push(`exclude_uid=${encodeURIComponent(excludeUid)}`);
        return this.get(`/admin/acpp/module6/copyable-plans?${params.join("&")}`);
    }

    async acppHodSubmitModule6ToAdmin(payload) {
        return this.post('/admin/acpp/module/teacherPlanningWorkflow/hod-submit-to-admin', payload);
    }

    async acppHodSendBackModule6ToTeacher(payload) {
        return this.post('/admin/acpp/module/teacherPlanningWorkflow/hod-send-back-teacher', payload);
    }

    async acppAdminApproveModule6(payload) {
        return this.post('/admin/acpp/module/teacherPlanningWorkflow/admin-approve', payload);
    }

    async acppAdminSendBackModule6ToHod(payload) {
        return this.post('/admin/acpp/module/teacherPlanningWorkflow/admin-send-back-hod', payload);
    }

    // ── Shared approvals API (assessments, activities, events, ...) ─────────
    async approvalsQueue(type, params = {}) {
        return this.getWithParams(`/approvals/${type}/queue`, params);
    }

    async approvalsDetail(type, id) {
        return this.get(`/approvals/${type}/${encodeURIComponent(id)}`);
    }

    // body: { action: 'approve'|'request_changes'|'override_hod', reason, confirm_override, send_back_to, expected_revision }
    async approvalsReview(type, id, body) {
        return this.post(`/approvals/${type}/${encodeURIComponent(id)}/review`, body);
    }

    // body: { items: [{ id, expected_revision }], action: 'approve' }
    async approvalsBulk(type, body) {
        return this.post(`/approvals/${type}/bulk-review`, body);
    }

    async approvalsUnpublish(type, id) {
        return this.post(`/approvals/${type}/${encodeURIComponent(id)}/unpublish`, {});
    }

    // ── Module 6 review dashboard (gated workflow) ──────────────────────────
    async acppM6Queue(params = {}) {
        return this.getWithParams('/admin/acpp/m6/queue', params);
    }

    async acppM6PlanDetail(uid, academicYear = '') {
        const q = academicYear ? `?academic_year=${encodeURIComponent(academicYear)}` : '';
        return this.get(`/admin/acpp/m6/plans/${encodeURIComponent(uid)}${q}`);
    }

    // payload: { action: 'approve'|'request_changes', reason, send_back_to, expected_revision, academic_year }
    async acppM6Review(uid, payload) {
        return this.post(`/admin/acpp/m6/plans/${encodeURIComponent(uid)}/review`, payload);
    }

    // payload: { items: [{ uid, expected_revision }], note, academic_year }
    async acppM6BulkApprove(payload) {
        return this.post('/admin/acpp/m6/bulk-review', { action: 'approve', ...payload });
    }
}

export default new AdminAPI();
